import React, { useState } from "react";
import { OverlayView } from "@react-google-maps/api";
import { Chip, IconButton } from "@mui/material";
import ContentCutIcon from "@mui/icons-material/Close";

import { DroppedItem } from "./types";
import { CommodeIcon } from "../../components/icons/Commode";
import { KitchenBurnerIcon } from "../../components/icons/KitchenBurner";
import { Pillar } from "../../components/icons/Pillar";
import { EntranceIcon } from "../../components/icons/Entrance";
import { getColorHexCode } from "../../utils/helpers/getColorHexCode";

export type Corner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

export interface DroppedItemsOverlayProps {
  droppedItems: DroppedItem[];

  onMouseDown: (e: React.MouseEvent<HTMLDivElement>, itemId: string) => void;
  onTouchStart: (e: React.TouchEvent<HTMLDivElement>, itemId: string) => void;
  onContextMenu: (e: React.MouseEvent, itemId: string) => void;

  onResizeMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => void;
  onResizeTouchStart: (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string,
    corner: Corner
  ) => void;

  onRotateMouseDown: (
    e: React.MouseEvent<HTMLDivElement>,
    itemId: string
  ) => void;
  onRotateTouchStart: (
    e: React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => void;

  onCutItem: (itemId: string) => void;

  colors?: any[];
}

const DroppedItemsOverlay: React.FC<DroppedItemsOverlayProps> = ({
  droppedItems,
  onMouseDown,
  onTouchStart,
  onContextMenu,
  onResizeMouseDown,
  onResizeTouchStart,
  onRotateMouseDown,
  onRotateTouchStart,
  onCutItem,
  colors,
}) => {
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const handleSelect = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>,
    itemId: string
  ) => {
    e.stopPropagation();
    setSelectedItemId(itemId);
  };

  // A little dot for rotation handle
  const rotateHandleStyle: React.CSSProperties = {
    position: "absolute",
    top: "-20px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "16px",
    height: "16px",
    backgroundColor: "#fff",
    border: "2px solid #4c9ffe",
    borderRadius: "50%",
    cursor: "grab",
    zIndex: 1000,
    touchAction: "none",
  };

  const getDefaultDimensions = (type: string) => {
    return { width: 50, height: 50 };
    console.log(type);
  };

  return (
    <>
      {droppedItems.map((item) => {
        const isSelected = selectedItemId === item.id;
        const isRoom = item.type === "Room";

        const width = item.width || getDefaultDimensions(item.type).width;
        const height = item.height || getDefaultDimensions(item.type).height;

        return (
          <OverlayView
            key={item.id}
            mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            position={{ lat: item.lat ?? 0, lng: item.lng ?? 0 }}
          >
            <>
              {isRoom ? (
                <div
                  id={item.id}
                  style={{
                    position: "relative",
                    transform: `translate(-50%, -50%) rotate(${
                      item.rotation || 0
                    }deg)`,
                    transformOrigin: "center center",
                    width,
                    height,
                    userSelect: "none",
                    cursor: "grab",
                  }}
                  // Mouse-based drag
                  onMouseDown={(e) => {
                    handleSelect(e, item.id);
                    onMouseDown(e, item.id);
                  }}
                  onTouchStart={(e) => {
                    handleSelect(e, item.id);
                    onTouchStart(e, item.id);
                  }}
                  onContextMenu={(e) => onContextMenu(e, item.id)}
                >
                  <div
                    style={{
                      backgroundColor: "#444",
                      color: "white",
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                    }}
                  >
                    <Chip
                      label={item.displayName}
                      sx={{
                        color: "white",
                        backgroundColor: "transparent",
                        fontWeight: "bold",
                        pointerEvents: "none",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                      }}
                    />
                  </div>

                  {isSelected && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          border: "2px solid #4c9ffe",
                          boxSizing: "border-box",
                          pointerEvents: "none",
                        }}
                      />

                      <div
                        style={rotateHandleStyle}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onRotateMouseDown(e, item.id);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onRotateTouchStart(e, item.id);
                        }}
                      />

                      <div
                        style={{
                          position: "absolute",
                          top: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nwse-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "topLeft");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "topLeft");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nesw-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "topRight");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "topRight");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nesw-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "bottomLeft");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "bottomLeft");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nwse-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "bottomRight");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "bottomRight");
                        }}
                      />

                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: "-35px",
                          right: "-35px",
                          color: "#fff",
                          backgroundColor: "rgba(187,0,0,0.9)",
                          ":hover": { backgroundColor: "rgba(187,0,0,0.7)" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCutItem(item.id);
                        }}
                      >
                        <ContentCutIcon fontSize="inherit" />
                      </IconButton>
                    </>
                  )}
                </div>
              ) : (
                <div
                  id={item.id}
                  style={{
                    position: "relative",
                    transform: `translate(-50%, -50%) rotate(${
                      item.rotation || 0
                    }deg)`,
                    transformOrigin: "center center",
                    cursor: "pointer",
                    userSelect: "none",
                    touchAction: "none",
                    width,
                    height,
                  }}
                  onMouseDown={(e) => {
                    handleSelect(e, item.id);
                    onMouseDown(e, item.id);
                  }}
                  onTouchStart={(e) => {
                    handleSelect(e, item.id);
                    onTouchStart(e, item.id);
                  }}
                  onContextMenu={(e) => onContextMenu(e, item.id)}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      position: "relative",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {item.type === "toiletSeat" && (
                      <CommodeIcon
                        width={width}
                        height={height}
                        colour={getColorHexCode(item.color ?? "", colors ?? [])}
                      />
                    )}
                    {item.type === "kitchenBurnerSlab" && (
                      <KitchenBurnerIcon
                        width={width}
                        height={height}
                        colour={getColorHexCode(item.color ?? "", colors ?? [])}
                      />
                    )}
                    {item.type === "housePillar" && (
                      <Pillar
                        width={width}
                        height={height}
                        colour={getColorHexCode(item.color ?? "", colors ?? [])}
                      />
                    )}
                    {item.type === "entrances" && (
                      <EntranceIcon
                        width={width}
                        height={height}
                        colour={getColorHexCode(item.color ?? "", colors ?? [])}
                      />
                    )}
                    {item.type === "wall" && (
                      <div
                        style={{
                          backgroundColor: "#444",
                          color: "white",
                          width: "100%",
                          height: "100%",
                          position: "relative",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                        }}
                      />
                    )}
                  </div>

                  {isSelected && (
                    <>
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          border: "2px solid #4c9ffe",
                          boxSizing: "border-box",
                          pointerEvents: "none",
                        }}
                      />

                      <div
                        style={rotateHandleStyle}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onRotateMouseDown(e, item.id);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onRotateTouchStart(e, item.id);
                        }}
                      />

                      <div
                        style={{
                          position: "absolute",
                          top: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nwse-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "topLeft");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "topLeft");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          top: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nesw-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "topRight");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "topRight");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          left: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nesw-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "bottomLeft");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "bottomLeft");
                        }}
                      />
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          right: "-5px",
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fff",
                          border: "2px solid #4c9ffe",
                          cursor: "nwse-resize",
                        }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          onResizeMouseDown(e, item.id, "bottomRight");
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          onResizeTouchStart(e, item.id, "bottomRight");
                        }}
                      />

                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: "-35px",
                          right: "-35px",
                          color: "#fff",
                          backgroundColor: "rgba(187,0,0,0.9)",
                          ":hover": { backgroundColor: "rgba(187,0,0,0.7)" },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onCutItem(item.id);
                        }}
                      >
                        <ContentCutIcon fontSize="inherit" />
                      </IconButton>
                    </>
                  )}
                </div>
              )}
            </>
          </OverlayView>
        );
      })}
    </>
  );
};

export default DroppedItemsOverlay;
